import { GreenGiffColored } from '@teko/ds-icon';
import { Image, Shape, SubTitle, TabItem, Tabs, styled } from '@teko/ds-mobile';
import { useProductDetailState } from 'contexts';
import { useTenantConfig } from 'hooks';
import { t } from 'i18n';
import { useMemo } from 'react';

type PaymentPromotionItems = {
  logoUrl: string;
  altLogo?: string;
  content: string;
  expirationDate?: string;
};

export const PaymentPromotion: React.FC = () => {
  const LOGO_DIMENSION = 48;

  const {
    paymentPromotions: workloadPromotions = [] as PaymentPromotionItems[],
    isMobile,
    epicOmni1643,
  } = useTenantConfig();

  const { paymentPromotionPolicies = [] } = useProductDetailState();

  const paymentPromotions = useMemo(() => {
    if (paymentPromotionPolicies.length)
      return paymentPromotionPolicies.map(
        (policy) =>
          ({
            logoUrl: policy.icon,
            content: policy.content,
          } as PaymentPromotionItems),
      );
    return workloadPromotions;
  }, [paymentPromotionPolicies, workloadPromotions]);

  if (!paymentPromotions?.length) return null;

  return (
    <StyledShape
      margin={epicOmni1643 ? 's0' : ['s16', 's0']}
      /**
       * SGC: 16px for both mobile and desktop
       * Others (epicOmni1643 = true): 8px for mobile, 16px for desktop
       */
      marginTop={
        epicOmni1643 ? (isMobile ? 's8' : 's16') : isMobile ? 's16' : 's8'
      }
    >
      <StyledHeaderContent>
        {!epicOmni1643 && <GreenGiffColored size={20} />}
        <SubTitle level={2}> {t('Ưu đãi thanh toán')}</SubTitle>
      </StyledHeaderContent>
      <StyledTabs>
        {paymentPromotions.map((item) => {
          return (
            <TabItem
              key={item.logoUrl}
              icon={
                <Image
                  src={item.logoUrl}
                  alt={item.altLogo || item.logoUrl}
                  width={LOGO_DIMENSION}
                />
              }
            >
              <StyledContent
                dangerouslySetInnerHTML={{ __html: item.content || '' }}
              />
              {item.expirationDate && (
                <StyledExpirationDate>
                  {t('HSD:')} {item.expirationDate}
                </StyledExpirationDate>
              )}
            </TabItem>
          );
        })}
      </StyledTabs>
    </StyledShape>
  );
};

const StyledShape = styled(Shape)`
  border: 1px solid;
  border-color: ${({ theme }) => theme.colors.border};
  border-radius: 8px;
  background-color: #fff;
`;

const StyledHeaderContent = styled.div`
  padding: 8px;
  background-color: #f5f5f5;
  border-top-left-radius: 8px;
  border-top-right-radius: 8px;
  display: flex;
  align-items: center;
  gap: 4px;
`;

const StyledTabs = styled(Tabs)`
  padding: 8px;

  > div[type='line'] {
    margin-bottom: 8px;
    gap: 16px;

    > div[type='line'] {
      padding: 0;
    }

    &::after {
      width: 48px;
    }
  }
`;

const StyledContent = styled.div`
  ul {
    padding-left: 2rem;
  }

  a {
    text-decoration: none;
  }

  font-size: 13px;
  line-height: 20px;
`;

const StyledExpirationDate = styled.div`
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-top: 8px;
  font-size: 12px;
  line-height: 16px;
`;
